import React, { useState, useEffect } from 'react';
import { 
  ShieldAlert, Radio, Crosshair, Radar, AlertTriangle, 
  MapPin, Battery, Wifi, Activity, Play, Volume2, 
  RefreshCw, CheckCircle2, ChevronRight, Zap, Target, Navigation,
  X, ExternalLink, Maximize2
} from 'lucide-react';

interface SensorNode {
  id: string;
  name: string;
  type: 'CIVILIAN' | 'MILITARY';
  location: string;
  lat: number;
  lon: number;
  alt: number;
  battery: number;
  power: string;
  status: 'ONLINE' | 'ACTIVE' | 'OFFLINE';
}

interface TargetData {
  id: string;
  type: 'SHAHED_136' | 'BALLISTIC_LAUNCH' | 'CRUISE_MISSILE';
  title: string;
  lat: number;
  lon: number;
  alt_m: number;
  speed_kmh: number;
  course_deg: number;
  confidence: number;
  eta_sec: number;
  bpf_freq: number;
  launch_point?: { lat: number; lon: number; name: string };
  mvg_lead_point?: { azimuth: number; elevation: number; range_m: number; lead_sec: number };
}

export const AdminTrembitaMeshProject: React.FC<{
  onBack: () => void;
  language?: string;
  currentUserEmail?: string;
}> = ({ onBack, language = 'uk', currentUserEmail }) => {
  const [selectedSubTab, setSelectedSubTab] = useState<'realtime' | 'rocket-launch' | 'mvg-targeting' | 'network-nodes'>('realtime');
  const [isAlertActive, setIsAlertActive] = useState<boolean>(true);
  const [simActive, setSimActive] = useState<boolean>(true);
  const [showMapModal, setShowMapModal] = useState<boolean>(false);

  // Сенсорні бокси
  const [nodes, setNodes] = useState<SensorNode[]>([
    { id: 'BOX-CIV-001', name: 'Балкон (вул. Доківська, 10)', type: 'CIVILIAN', location: 'смт. Коцюбинське', lat: 50.4892, lon: 30.3315, alt: 165, battery: 100, power: '220V + LiFePO4', status: 'ACTIVE' },
    { id: 'BOX-CIV-002', name: 'Паркан садиби (Ірпінь)', type: 'CIVILIAN', location: 'м. Ірпінь', lat: 50.5180, lon: 30.2450, alt: 150, battery: 98, power: 'Сонячна панель MPPT', status: 'ACTIVE' },
    { id: 'BOX-CIV-003', name: 'Дах ліцею (Святошин)', type: 'CIVILIAN', location: 'м. Київ (Святошин)', lat: 50.4550, lon: 30.3680, alt: 180, battery: 100, power: 'Ethernet PoE', status: 'ACTIVE' },
    { id: 'BOX-CIV-004', name: 'Дачна ділянка (Буча, ліс)', type: 'CIVILIAN', location: 'м. Буча', lat: 50.5480, lon: 30.2150, alt: 145, battery: 87, power: 'Powerbank USB-C', status: 'ACTIVE' },
    { id: 'BOX-MIL-005', name: 'Позиція РЕБ / Спостережний пункт', type: 'MILITARY', location: 'Посадка МВГ-1', lat: 50.4720, lon: 30.2750, alt: 155, battery: 92, power: 'Автомобіль 12В', status: 'ACTIVE' },
    { id: 'BOX-MIL-006', name: 'Панівна висота 172', type: 'MILITARY', location: 'Сектор МВГ-2', lat: 50.5150, lon: 30.3600, alt: 172, battery: 95, power: 'LiFePO4 48 год', status: 'ACTIVE' },
  ]);

  // Поточні виявлені цілі
  const [activeTargets, setActiveTargets] = useState<TargetData[]>([
    {
      id: 'TRK-20261017-01',
      type: 'SHAHED_136',
      title: 'Ударний БПЛА «Shahed-136 / Герань-2»',
      lat: 50.4952,
      lon: 30.4245,
      alt_m: 135,
      speed_kmh: 178.5,
      course_deg: 138,
      confidence: 0.96,
      eta_sec: 42,
      bpf_freq: 38.52,
      mvg_lead_point: {
        azimuth: 142.5,
        elevation: 14.8,
        range_m: 2350,
        lead_sec: 3.4
      }
    },
    {
      id: 'TRK-LAUNCH-09',
      type: 'BALLISTIC_LAUNCH',
      title: 'ЗАСІКАНО СТАРТ БАЛІСТИЧНОЇ РАКЕТИ (СЕЙСМО-ІНФРАЗВУК)',
      lat: 50.8120,
      lon: 31.1250,
      alt_m: 850,
      speed_kmh: 3200,
      course_deg: 215,
      confidence: 0.98,
      eta_sec: 110,
      bpf_freq: 2.1,
      launch_point: {
        lat: 52.1245,
        lon: 31.8450,
        name: 'Район пуску: кордон Чернігівщина / Брянська обл. (Координати ПУ виявлено)'
      }
    }
  ]);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col font-sans">
      {/* Верхня панель адміна */}
      <header className="bg-slate-900/90 border-b border-slate-800 px-6 py-4 flex flex-wrap items-center justify-between gap-4 backdrop-blur-md sticky top-0 z-50">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-red-950/80 border border-red-600/40 text-red-400 shadow-lg shadow-red-950/50">
            <Radar className="w-6 h-6 animate-spin text-red-500" style={{ animationDuration: '8s' }} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-lg font-black tracking-wider text-white uppercase font-mono">
                ТРЕМБІТА-МЕШ <span className="text-red-500 font-sans font-bold text-xs px-2 py-0.5 bg-red-950/60 rounded border border-red-500/30">ТІЛЬКИ ДЛЯ АДМІНІСТРАТОРІВ</span>
              </h1>
              <span className="flex h-2.5 w-2.5 relative">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
              </span>
            </div>
            <p className="text-xs text-slate-400">
              Децентралізована мережа акустично-інфразвукового виявлення дронів та старту ракет | Автор: Банах А.С.
            </p>
          </div>
        </div>

        {/* Навігаційні кнопки підрозділів */}
        <div className="flex items-center gap-2 bg-slate-950 p-1 rounded-xl border border-slate-800">
          <button
            onClick={() => setSelectedSubTab('realtime')}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
              selectedSubTab === 'realtime' ? 'bg-red-600 text-white shadow-md' : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            <Crosshair className="w-4 h-4" />
            <span>Радар БПЛА</span>
          </button>
          <button
            onClick={() => setSelectedSubTab('rocket-launch')}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
              selectedSubTab === 'rocket-launch' ? 'bg-amber-600 text-white shadow-md' : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            <Zap className="w-4 h-4 text-yellow-300" />
            <span>Старт Ракет (Сейсмо/Інфра)</span>
          </button>
          <button
            onClick={() => setSelectedSubTab('mvg-targeting')}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
              selectedSubTab === 'mvg-targeting' ? 'bg-emerald-600 text-white shadow-md' : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            <Target className="w-4 h-4 text-emerald-300" />
            <span>Цілевказівка МВГ</span>
          </button>
          <button
            onClick={() => setSelectedSubTab('network-nodes')}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
              selectedSubTab === 'network-nodes' ? 'bg-sky-600 text-white shadow-md' : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            <Radio className="w-4 h-4" />
            <span>Мережа Боксів (6)</span>
          </button>
        </div>

        <button
          onClick={onBack}
          className="px-3.5 py-1.5 rounded-lg text-xs font-semibold bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 transition cursor-pointer"
        >
          ← Назад у Hub
        </button>
      </header>

      {/* Головна панель вмісту */}
      <main className="flex-1 p-6 space-y-6 max-w-7xl mx-auto w-full">
        {/* Панель бойової тривоги */}
        {isAlertActive && (
          <div className="bg-red-950/40 border border-red-500/60 rounded-2xl p-4 flex flex-wrap items-center justify-between gap-4 shadow-xl shadow-red-950/30">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-red-600 rounded-xl text-white animate-pulse">
                <AlertTriangle className="w-6 h-6" />
              </div>
              <div>
                <div className="text-red-400 font-bold text-sm uppercase tracking-wide flex items-center gap-2">
                  <span>БОЙОВА ТРИВОГА: НИЗЬКОЛІТАЮЧА ЦІЛЬ У ЗОНІ ВІДПОВІДАЛЬНОСТІ</span>
                  <span className="text-xs bg-red-600 text-white px-2 py-0.5 rounded-full font-mono">ТАКТИЧНИЙ СЕКТОР КИЇВ-ЗАХІД</span>
                </div>
                <p className="text-xs text-slate-300 mt-0.5">
                  Виявлено акустичну субгармоніку Shahed-136 (38.52 Гц). 6 сенсорних боксів беруть участь у 3D TDoA розрахунку.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="text-right font-mono">
                <div className="text-xs text-slate-400">Час до рубежу МВГ:</div>
                <div className="text-xl font-black text-amber-400">42 сек</div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setShowMapModal(true)}
                  className="px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-xl text-xs font-bold transition flex items-center gap-2 shadow-lg shadow-red-600/30 cursor-pointer"
                  title="Відкрити інтерактивну карту TDoA"
                >
                  <Radar className="w-4 h-4 animate-spin" style={{ animationDuration: '6s' }} />
                  <span>Відкрити Повну Карту TDoA</span>
                  <ChevronRight className="w-4 h-4" />
                </button>
                <a
                  href="/trembita/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white rounded-xl text-xs font-bold transition border border-slate-700 flex items-center justify-center cursor-pointer"
                  title="Відкрити карту в окремій вкладці браузера"
                >
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        )}

        {/* ВКЛАДКА 1: РАДАР БПЛА ТА СПОСТЕРЕЖЕННЯ */}
        {selectedSubTab === 'realtime' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Ліва колонка: Параметри цілі в польоті */}
            <div className="lg:col-span-1 space-y-4">
              <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5 shadow-lg space-y-4">
                <div className="flex items-center justify-between border-b border-slate-800 pb-3">
                  <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Параметри розрахунку 3D TDoA</span>
                  <span className="text-xs px-2.5 py-0.5 rounded-full bg-emerald-950 text-emerald-400 border border-emerald-500/30 font-mono font-bold">
                    GDOP: 1.38 (Відмінно)
                  </span>
                </div>

                <div className="space-y-3 font-mono text-sm">
                  <div className="flex justify-between items-center p-2.5 rounded-lg bg-slate-950/60 border border-slate-800/80">
                    <span className="text-slate-400 text-xs">Тип загрози:</span>
                    <span className="text-amber-400 font-bold">Shahed-136 (96%)</span>
                  </div>
                  <div className="flex justify-between items-center p-2.5 rounded-lg bg-slate-950/60 border border-slate-800/80">
                    <span className="text-slate-400 text-xs">Поточна швидкість:</span>
                    <span className="text-white font-bold">178.5 км/год (49.6 м/с)</span>
                  </div>
                  <div className="flex justify-between items-center p-2.5 rounded-lg bg-slate-950/60 border border-slate-800/80">
                    <span className="text-slate-400 text-xs">Висота польоту (Alt):</span>
                    <span className="text-emerald-400 font-bold">135 м над землею</span>
                  </div>
                  <div className="flex justify-between items-center p-2.5 rounded-lg bg-slate-950/60 border border-slate-800/80">
                    <span className="text-slate-400 text-xs">Курс (Азимут польоту):</span>
                    <span className="text-sky-400 font-bold">138° (Південний Схід)</span>
                  </div>
                  <div className="flex justify-between items-center p-2.5 rounded-lg bg-slate-950/60 border border-slate-800/80">
                    <span className="text-slate-400 text-xs">Лопатева частота BPF:</span>
                    <span className="text-purple-400 font-bold">38.52 Гц</span>
                  </div>
                  <div className="flex justify-between items-center p-2.5 rounded-lg bg-slate-950/60 border border-slate-800/80">
                    <span className="text-slate-400 text-xs">Точність геоприв'язки:</span>
                    <span className="text-emerald-400 font-bold">± 0.38 метра</span>
                  </div>
                </div>

                <div className="pt-2 border-t border-slate-800">
                  <div className="text-xs text-slate-400 mb-2 font-bold uppercase">Вузли, що здійснюють захват звуку:</div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <span className="p-1.5 rounded bg-slate-950 text-slate-300 border border-slate-800 flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-emerald-400"></span> Доківська, 10
                    </span>
                    <span className="p-1.5 rounded bg-slate-950 text-slate-300 border border-slate-800 flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-emerald-400"></span> Ірпінь (сад)
                    </span>
                    <span className="p-1.5 rounded bg-slate-950 text-slate-300 border border-slate-800 flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-emerald-400"></span> Святошин (дах)
                    </span>
                    <span className="p-1.5 rounded bg-slate-950 text-slate-300 border border-slate-800 flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-emerald-400"></span> Буча (дача)
                    </span>
                    <span className="p-1.5 rounded bg-slate-950 text-slate-300 border border-slate-800 flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-emerald-400"></span> МВГ-1 (посадка)
                    </span>
                    <span className="p-1.5 rounded bg-slate-950 text-slate-300 border border-slate-800 flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-emerald-400"></span> МВГ-2 (висота)
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Права колонка: Інтерактивний радарний монітор */}
            <div className="lg:col-span-2 bg-slate-900 border border-slate-800 rounded-2xl p-5 shadow-lg flex flex-col justify-between">
              <div className="flex items-center justify-between border-b border-slate-800 pb-3">
                <div className="flex items-center gap-2">
                  <Activity className="w-4 h-4 text-emerald-400" />
                  <span className="text-xs font-bold text-white uppercase tracking-wider">Тактичний радарний індикатор реального часу</span>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setShowMapModal(true)}
                    className="px-2.5 py-1 bg-slate-800 hover:bg-slate-700 text-sky-400 border border-slate-700 rounded-lg text-xs font-medium transition flex items-center gap-1 cursor-pointer"
                    title="Розгорнути повну інтерактивну карту TDoA"
                  >
                    <Maximize2 className="w-3.5 h-3.5" />
                    <span>Повна карта</span>
                  </button>
                  <div className="text-xs text-slate-400 font-mono hidden sm:block">
                    Оновлення TDoA: кожні 0.5 с | Сервер: Contabo (Порт 8090)
                  </div>
                </div>
              </div>

              {/* Візуалізація радарного кола */}
              <div className="relative my-6 flex items-center justify-center">
                <div className="w-80 h-80 rounded-full border-2 border-emerald-500/30 relative flex items-center justify-center bg-slate-950/80 shadow-2xl">
                  {/* Концентричні кола */}
                  <div className="w-60 h-60 rounded-full border border-emerald-500/20 absolute"></div>
                  <div className="w-40 h-40 rounded-full border border-emerald-500/20 absolute"></div>
                  <div className="w-20 h-20 rounded-full border border-emerald-500/20 absolute"></div>
                  
                  {/* Осі */}
                  <div className="w-full h-px bg-emerald-500/20 absolute"></div>
                  <div className="h-full w-px bg-emerald-500/20 absolute"></div>

                  {/* Стрілка сканування */}
                  <div className="w-40 h-40 absolute origin-bottom-right top-0 left-0 border-r-2 border-emerald-400/60 animate-spin" style={{ animationDuration: '4s' }}></div>

                  {/* Центральна точка (Командний пункт) */}
                  <div className="w-3 h-3 rounded-full bg-sky-400 border border-white z-10 shadow-lg shadow-sky-400"></div>

                  {/* Точка БПЛА Shahed-136 */}
                  <div className="absolute top-28 right-24 flex items-center gap-2">
                    <span className="w-4 h-4 rounded-full bg-red-600 border-2 border-yellow-300 animate-ping absolute"></span>
                    <span className="w-4 h-4 rounded-full bg-red-600 border-2 border-yellow-300 relative"></span>
                    <span className="text-xs font-mono font-bold text-red-400 bg-slate-950/80 px-2 py-0.5 rounded border border-red-500/40">
                      SHAHED (135м, 178км/г)
                    </span>
                  </div>

                  {/* Позначки боксів навколо */}
                  <div className="absolute top-12 left-16 text-[10px] text-sky-400 bg-slate-950/60 px-1 rounded">BOX-001 (Доківська)</div>
                  <div className="absolute top-16 right-16 text-[10px] text-sky-400 bg-slate-950/60 px-1 rounded">BOX-002 (Ірпінь)</div>
                  <div className="absolute bottom-16 left-20 text-[10px] text-sky-400 bg-slate-950/60 px-1 rounded">BOX-003 (Святошин)</div>
                  <div className="absolute bottom-12 right-20 text-[10px] text-emerald-400 bg-slate-950/60 px-1 rounded">МВГ-1 (Посадка)</div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3 border-t border-slate-800 pt-3 text-center text-xs">
                <div className="p-2 rounded-lg bg-slate-950 border border-slate-800">
                  <div className="text-slate-400">Швидкість поширення звуку:</div>
                  <div className="font-mono font-bold text-white">340.29 м/с (15°C)</div>
                </div>
                <div className="p-2 rounded-lg bg-slate-950 border border-slate-800">
                  <div className="text-slate-400">Синхронізація датчиків:</div>
                  <div className="font-mono font-bold text-emerald-400">&lt; 15 нс (GNSS 1PPS)</div>
                </div>
                <div className="p-2 rounded-lg bg-slate-950 border border-slate-800">
                  <div className="text-slate-400">Час обчислення на сервері:</div>
                  <div className="font-mono font-bold text-sky-400">2.8 мілісекунди</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ВКЛАДКА 2: ЗАСІКАННЯ СТАРТУ РАКЕТ (СЕЙСМО/ІНФРАЗВУК) */}
        {selectedSubTab === 'rocket-launch' && (
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-xl space-y-6">
            <div className="flex items-center justify-between border-b border-slate-800 pb-4">
              <div>
                <h2 className="text-base font-bold text-amber-400 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-amber-400" />
                  <span>МОНІТОРИНГ СТАРТУ БАЛІСТИЧНИХ ТА ЗЕНІТНИХ РАКЕТ (С-300 / С-400 / ІСКАНДЕР)</span>
                </h2>
                <p className="text-xs text-slate-400 mt-1">
                  Дворівнева реєстрація: підземна сейсмічна хвиля P (ГЦСК, ~5000 м/с) + атмосферний інфразвуковий фронт боксів «Трембіта» (340 м/с).
                </p>
              </div>
              <span className="px-3 py-1 rounded-full bg-amber-950 border border-amber-500/40 text-amber-300 text-xs font-mono font-bold">
                СЕЙСМО-АКУСТИЧНИЙ РУБІЖ 1-ГО ЕШЕЛОНУ
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Картка зафіксованого пуску */}
              <div className="bg-slate-950 border border-amber-500/40 rounded-xl p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-red-400 uppercase tracking-wider">ФІКСАЦІЯ МОМЕНТУ СТАРТУ</span>
                  <span className="text-xs font-mono bg-red-950 text-red-300 px-2 py-0.5 rounded border border-red-500/30">ТРИВОГА: 110 сек</span>
                </div>

                <div className="space-y-2.5 font-mono text-xs">
                  <div className="flex justify-between p-2 rounded bg-slate-900 border border-slate-800">
                    <span className="text-slate-400">Час детекції пуску:</span>
                    <span className="text-white font-bold">17.10.2026 20:28:14 UTC</span>
                  </div>
                  <div className="flex justify-between p-2 rounded bg-slate-900 border border-slate-800">
                    <span className="text-slate-400">Район стартової позиції ПУ:</span>
                    <span className="text-amber-400 font-bold">Брянська обл. (Координати ПУ виявлено)</span>
                  </div>
                  <div className="flex justify-between p-2 rounded bg-slate-900 border border-slate-800">
                    <span className="text-slate-400">Енергія інфразвукового імпульсу:</span>
                    <span className="text-white font-bold">142.5 дБ (в діапазоні 0.8–2.4 Гц)</span>
                  </div>
                  <div className="flex justify-between p-2 rounded bg-slate-900 border border-slate-800">
                    <span className="text-slate-400">Швидкість ракети на траєкторії:</span>
                    <span className="text-red-400 font-bold">3 200 км/год (Балістична траса)</span>
                  </div>
                  <div className="flex justify-between p-2 rounded bg-slate-900 border border-slate-800">
                    <span className="text-slate-400">Ймовірний сектор підльоту:</span>
                    <span className="text-yellow-400 font-bold">Київська область (Північний сектор)</span>
                  </div>
                </div>

                <div className="p-3 bg-amber-950/30 border border-amber-500/30 rounded-lg text-xs text-amber-200 leading-relaxed">
                  💡 <b>Фізичний принцип:</b> Факел ракетного двигуна створює гігантську низькочастотну акустичну хвилю, яка фіксується мережею боксів «Трембіта» за 40–80 км до того, як ракета увійде в зону дії наземних РЛС!
                </div>
              </div>

              {/* Відповідь: Чи потрібні сейсмодатчики в боксі? */}
              <div className="bg-slate-950 border border-slate-800 rounded-xl p-5 space-y-4">
                <h3 className="text-xs font-bold text-sky-400 uppercase tracking-wider">
                  ЧОМУ СЕЙСМОДАТЧИК НЕ ПОТРІБЕН У КОЖНОМУ НАРОДНОМУ БОКСІ:
                </h3>
                
                <ul className="space-y-3 text-xs text-slate-300 leading-relaxed">
                  <li className="flex items-start gap-2">
                    <span className="text-red-400 font-bold">✗</span>
                    <span><b>Шуми міського середовища:</b> Сейсмометр на балконі чи паркані буде ловити вібрації від дверей, трамваїв, пральних машин та кроків мешканців, викликаючи хибні спрацювання.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-400 font-bold">✗</span>
                    <span><b>Здорожчання виробу:</b> Якісний 3-осьовий геофон/акселерометр збільшив би ціну приладу з $160 до $400+.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-emerald-400 font-bold">✓</span>
                    <span><b>Інфразвуковий датчик нашого боксу (0.5–45 Гц) вже чудово ловить ударну хвилю старту:</b> Потужність акустичного удару ракети досягає сотень кілометрів по повітрю!</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-emerald-400 font-bold">✓</span>
                    <span><b>Синергія з ГЦСК:</b> Опорні сейсмометри Головного центру спеціального контролю (ГЦСК), закопані на глибину 2 метри, дають точний момент T0 підземної хвилі, а наша мережа дає азимут і координати!</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* ВКЛАДКА 3: ЦІЛЕВКАЗІВКА МОБІЛЬНИМ ВОГНЕВИМ ГРУПАМ (МВГ) */}
        {selectedSubTab === 'mvg-targeting' && (
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-xl space-y-6">
            <div className="flex items-center justify-between border-b border-slate-800 pb-4">
              <div>
                <h2 className="text-base font-bold text-emerald-400 flex items-center gap-2">
                  <Target className="w-5 h-5 text-emerald-400" />
                  <span>АВТОМАТИЗОВАНА ЦІЛЕВКАЗІВКА ДЛЯ РОЗРАХУНКІВ ППО (ПРОЖЕКТОРИ ТА КУЛЕМЕТИ)</span>
                </h2>
                <p className="text-xs text-slate-400 mt-1">
                  Пряма трансляція координат на тактичні планшети МВГ через WSS JSON та АСУ «Дельта».
                </p>
              </div>
              <button className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl text-xs font-bold transition flex items-center gap-2 shadow-lg shadow-emerald-600/30 cursor-pointer">
                <Navigation className="w-4 h-4" />
                <span>Надіслати азимут усім екіпажам МВГ</span>
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Прожекторний сектор */}
              <div className="bg-slate-950 border border-slate-800 rounded-xl p-5 space-y-3 text-center">
                <div className="text-xs text-slate-400 uppercase font-bold">Азимут для зенітного прожектора</div>
                <div className="text-4xl font-black text-amber-400 font-mono">142.5°</div>
                <div className="text-xs text-slate-400">Сектор наведення: Південний Схід (± 2.5°)</div>
                <div className="p-2 rounded bg-amber-950/40 border border-amber-500/30 text-amber-300 text-xs font-bold">
                  ГОТОВНІСТЬ ПРОЖЕКТОРА ДО ВМИКАННЯ
                </div>
              </div>

              {/* Кут підвищення (елевація) */}
              <div className="bg-slate-950 border border-slate-800 rounded-xl p-5 space-y-3 text-center">
                <div className="text-xs text-slate-400 uppercase font-bold">Кут підвищення (Кут місця)</div>
                <div className="text-4xl font-black text-sky-400 font-mono">+14.8°</div>
                <div className="text-xs text-slate-400">Розрахована висота цілі: 135 м</div>
                <div className="p-2 rounded bg-sky-950/40 border border-sky-500/30 text-sky-300 text-xs font-bold">
                  НИЗЬКОЛІТАЮЧА ЦІЛЬ (КУЛЕМЕТНИЙ РУБІЖ)
                </div>
              </div>

              {/* Точка випередження (Lead Point) */}
              <div className="bg-slate-950 border border-slate-800 rounded-xl p-5 space-y-3 text-center">
                <div className="text-xs text-slate-400 uppercase font-bold">Точка випередження (Browning / ДШК)</div>
                <div className="text-4xl font-black text-emerald-400 font-mono">3.4 сек</div>
                <div className="text-xs text-slate-400">Дистанція до точки зустрічі: 2 350 м</div>
                <div className="p-2 rounded bg-emerald-950/40 border border-emerald-500/30 text-emerald-300 text-xs font-bold">
                  ВІДКРИТИ ВОГОНЬ ЧЕРЕЗ 15 СЕКУНД
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ВКЛАДКА 4: РЕЄСТР ВУЗЛІВ («ВСТАНОВИВ І ЗАБУВ») */}
        {selectedSubTab === 'network-nodes' && (
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-xl space-y-4">
            <div className="flex items-center justify-between border-b border-slate-800 pb-4">
              <div>
                <h2 className="text-base font-bold text-white flex items-center gap-2">
                  <Radio className="w-5 h-5 text-sky-400" />
                  <span>РЕЄСТР АКТИВНИХ СЕНСОРНИХ БОКСІВ («ТРЕМБІТА-НАРОДНА»)</span>
                </h2>
                <p className="text-xs text-slate-400 mt-1">
                  Усі бокси автоматично геоприв'язані за GNSS 1PPS та підключені до центрального сервера.
                </p>
              </div>
              <span className="text-xs font-mono text-emerald-400 bg-emerald-950/60 px-3 py-1 rounded-full border border-emerald-500/30 font-bold">
                УСІ 6 ВУЗЛІВ В ОНЛАЙНІ (100% ГОТОВНІСТЬ)
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-xs text-left">
                <thead className="bg-slate-950 text-slate-400 uppercase text-[10px] tracking-wider border-b border-slate-800">
                  <tr>
                    <th className="p-3">ID Вузла</th>
                    <th className="p-3">Назва та Локація</th>
                    <th className="p-3">Тип</th>
                    <th className="p-3">Координати (Lat, Lon, Alt)</th>
                    <th className="p-3">Живлення</th>
                    <th className="p-3">Батарея</th>
                    <th className="p-3">Статус</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800 font-mono">
                  {nodes.map(n => (
                    <tr key={n.id} className="hover:bg-slate-800/40 transition">
                      <td className="p-3 font-bold text-white">{n.id}</td>
                      <td className="p-3 font-sans">
                        <div className="font-semibold text-slate-200">{n.name}</div>
                        <div className="text-[11px] text-slate-400">{n.location}</div>
                      </td>
                      <td className="p-3">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                          n.type === 'CIVILIAN' ? 'bg-sky-950 text-sky-400 border border-sky-500/30' : 'bg-emerald-950 text-emerald-400 border border-emerald-500/30'
                        }`}>
                          {n.type === 'CIVILIAN' ? 'НАРОДНИЙ' : 'ВІЙСЬКОВИЙ'}
                        </span>
                      </td>
                      <td className="p-3 text-slate-300">
                        {n.lat.toFixed(4)}, {n.lon.toFixed(4)} ({n.alt}м)
                      </td>
                      <td className="p-3 text-slate-300 font-sans">{n.power}</td>
                      <td className="p-3">
                        <span className="text-emerald-400 font-bold">{n.battery}%</span>
                      </td>
                      <td className="p-3">
                        <span className="flex items-center gap-1.5 text-emerald-400 font-bold">
                          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
                          ONLINE
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      {/* Модальне вікно з повною інтерактивною картою TDoA */}
      {showMapModal && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex flex-col p-2 md:p-6 animate-in fade-in duration-200">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl flex flex-col flex-1 overflow-hidden shadow-2xl">
            {/* Шапка модального вікна */}
            <div className="bg-slate-950 border-b border-slate-800 px-4 py-3 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-950/80 border border-red-500/40 rounded-xl text-red-400">
                  <Radar className="w-5 h-5 text-red-500 animate-spin" style={{ animationDuration: '6s' }} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-sm md:text-base font-bold text-white font-mono uppercase tracking-wider">
                      ТАКТИЧНИЙ СИТУАЦІЙНИЙ ЦЕНТР: СИСТЕМА «ТРЕМБІТА-МЕШ»
                    </h2>
                    <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-red-950 text-red-400 border border-red-500/30">
                      LIVE TDoA 3D
                    </span>
                  </div>
                  <p className="text-xs text-slate-400">
                    Повна інтерактивна карта мультилатерації, пеленгації та цілевказівки МВГ у реальному часі
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <a
                  href="/trembita/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-lg text-xs font-medium border border-slate-700 flex items-center gap-1.5 transition cursor-pointer"
                >
                  <ExternalLink className="w-3.5 h-3.5 text-sky-400" />
                  <span className="hidden sm:inline">Окреме вікно</span>
                </a>
                <button
                  type="button"
                  onClick={() => setShowMapModal(false)}
                  className="p-1.5 bg-slate-800 hover:bg-red-900/60 hover:text-red-300 text-slate-400 rounded-lg transition border border-slate-700 cursor-pointer"
                  title="Закрити"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Тіло модального вікна з картою */}
            <div className="flex-1 bg-slate-950 relative w-full h-full min-h-[500px]">
              <iframe
                src="/trembita/"
                title="Повна Карта Trembita Mesh TDoA"
                className="w-full h-full border-0 absolute inset-0"
                allow="geolocation"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
